// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SettingsDropdown from './SettingsDropdown';
import { useDashboardNav, isEmployerSubcontractorShellPath } from '../context/DashboardNavContext';
import { employerDashboardOrgTitle, subcontractorDashboardOrgTitle } from '../utils/userDashboardUi';
import './Header.css';

const OrganisationSettingsIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
    <path d="M6 12h12" />
    <path d="M6 16h12" />
    <path d="M10 6h.01" />
    <path d="M10 10h.01" />
    <path d="M14 6h.01" />
    <path d="M14 10h.01" />
  </svg>
);

const Header = ({ user, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { navItems } = useDashboardNav();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;
  const isDashboardActive = location.pathname === '/dashboard'
    || location.pathname === '/dashboard/'
    || location.pathname === '/dashboard/employer'
    || location.pathname === '/dashboard/subcontractor';

  const canUseSubcontractorDashboard = Boolean(
    user && Array.isArray(user.employers) && user.employers.length > 0
  );

  const isSuperAdminUser = Boolean(
    user && (user.role === 'superuser' || user.role === 'super_admin')
  );

  const showDashboardShellNav = Boolean(
    user &&
      !isSuperAdminUser &&
      navItems &&
      navItems.length > 0 &&
      isEmployerSubcontractorShellPath(location.pathname)
  );

  const isSubcontractorShellNav =
    showDashboardShellNav &&
    navItems?.some((i) => i.id === 'employers') &&
    navItems?.some((i) => i.id === 'payments');

  const pathNorm = (location.pathname || '/').replace(/\/$/, '') || '/';
  const onEmployerOrSubcontractorDashboardRoute =
    pathNorm === '/dashboard' ||
    pathNorm === '/dashboard/employer' ||
    pathNorm === '/dashboard/subcontractor';

  /** Employer + subcontractor dashboards, profile shell, etc. — always opens organisation settings only (no dashboard switching) */
  const showOrganisationSettingsButton =
    Boolean(user) &&
    !isSuperAdminUser &&
    (showDashboardShellNav || onEmployerOrSubcontractorDashboardRoute);

  // Menu links array
  const menuLinks = [
    { name: 'Pricing', path: '/pricing' },
    { name: 'Contractors', path: '/learn-more' },
    { name: 'Accountants', path: '/accountants' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const dashboardOrgTitle = (() => {
    if (!user || !showDashboardShellNav) return '';
    if (isSubcontractorShellNav) return subcontractorDashboardOrgTitle(user);
    return employerDashboardOrgTitle(user);
  })();

  return (
    <header className={`paycis-header header ${user ? 'header--logged-in' : ''}`}>
      <div className="header-container">
        {/* Logo on left */}
        <div className="logo-section">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img
              src="/images/apple-icon-57x57.png"
              alt="PayCIS Logo"
              className="paycis-logo logo-image"
            />
            {dashboardOrgTitle ? (
              <span className="logo-org-title" title={dashboardOrgTitle}>
                {dashboardOrgTitle}
              </span>
            ) : 
            (
              <span className="logo-org-title" title="PayCIS">
                PayCIS
              </span>
            )}
          </Link>
        </div>

        {/* Desktop Navigation - Hidden on mobile */}
        <nav className="desktop-navigation" aria-label="Main Navigation">
          <div className={`nav-links ${showDashboardShellNav ? 'nav-links--dashboard-shell' : ''}`}>
            {showDashboardShellNav ? (
              <>
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`nav-link header-dashboard-nav-link ${item.active ? 'active' : ''}`}
                    onClick={item.onActivate}
                  >
                    <span className="nav-text">{item.label}</span>
                    {item.badge != null && item.badge > 0 ? (
                      <span className="header-nav-badge">{item.badge}</span>
                    ) : null}
                  </button>
                ))}
              </>
            ) : (
              <>
                {menuLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                  >
                    <span className="nav-text">{link.name}</span>
                  </Link>
                ))}
                {user && (
                  <div className={`dashboard-nav-group ${isDashboardActive ? 'active' : ''}`}>
                    <Link to="/dashboard/employer" className={`nav-link ${isDashboardActive ? 'active' : ''}`}>
                      <span className="nav-text">DASHBOARD</span>
                    </Link>
                    {canUseSubcontractorDashboard && (
                      <div className="dashboard-nav-dropdown">
                        <Link to="/dashboard/employer" className="dashboard-nav-dropdown-link">Employer</Link>
                        <Link
                          to="/dashboard/subcontractor?menu=payments"
                          className="dashboard-nav-dropdown-link"
                        >
                          SubContractor
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Desktop Auth Buttons */}
          {!user && (
            <div className="auth-buttons desktop-only">
              <Link to="/register" className="paycis-gradient-btn">
                Try for free
              </Link>
              <Link to="/login" className="paycis-outline-btn">
                LOGIN
              </Link>
            </div>
          )}

          {/* Organisation settings + profile menu */}
          {user && (
            <div className="header-user-actions">
              {showOrganisationSettingsButton ? (
                <button
                  type="button"
                  className="settings-dropdown-button"
                  onClick={() => navigate('/profile/organization')}
                  aria-label="Organisation settings"
                  title="Organisation settings"
                >
                  <OrganisationSettingsIcon />
                </button>
              ) : null}
              <SettingsDropdown user={user} onLogout={onLogout} />
            </div>
          )}
        </nav>

        {/* Mobile Right Section: Login / profile icons + Hamburger */}
        <div className="mobile-right-section">
          {/* Mobile Login Button - Always visible */}
          {!user && (
            <Link to="/login" className="mobile-login-btn">
              LOGIN
            </Link>
          )}

          {user && (
            <div className="header-user-actions header-user-actions--mobile-bar">
              {showOrganisationSettingsButton ? (
                <button
                  type="button"
                  className="settings-dropdown-button"
                  onClick={() => navigate('/profile/organization')}
                  aria-label="Organisation settings"
                  title="Organisation settings"
                >
                  <OrganisationSettingsIcon />
                </button>
              ) : null}
              <SettingsDropdown user={user} onLogout={onLogout} />
            </div>
          )}
          
          {/* Mobile Menu Toggle - hide on dashboard shell (bottom nav is used there) */}
          {!showDashboardShellNav && (
            <div
              className="mobile-menu-toggle"
              onClick={toggleMenu}
              aria-label="Toggle navigation"
            >
              <div className={`hamburger ${isMenuOpen ? 'hamburger-open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="menu-text">Menu</span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay - Slide from right */}
      <div className={`mobile-menu-overlay ${isMenuOpen && !showDashboardShellNav ? 'active' : ''}`}>
        <div className="mobile-menu-header">
          <div className="mobile-menu-logo">
            <img
              src="/images/apple-icon-57x57.png"
              alt="PayCIS Logo"
              className="mobile-logo-image"
            />
          </div>
          <button className="mobile-menu-close" onClick={closeMenu} aria-label="Close menu">
            <span className="close-icon">✕</span>
            <span className="close-text">Close</span>
          </button>
        </div>
        
        <div className="mobile-menu-content">
          <div className="mobile-nav-links">
            {showDashboardShellNav ? (
              <>
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="mobile-nav-link mobile-nav-link--button"
                    onClick={() => {
                      item.onActivate();
                      closeMenu();
                    }}
                  >
                    {item.label}
                    {item.badge != null && item.badge > 0 ? (
                      <span className="header-nav-badge header-nav-badge--inline">{item.badge}</span>
                    ) : null}
                  </button>
                ))}
              </>
            ) : (
              <>
                {menuLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="mobile-nav-link"
                    onClick={closeMenu}
                  >
                    {link.name}
                  </Link>
                ))}
                {user && (
                  <>
                    <Link to="/dashboard/employer" className="mobile-nav-link" onClick={closeMenu}>DASHBOARD</Link>
                    {canUseSubcontractorDashboard && (
                      <Link
                        to="/dashboard/subcontractor?menu=payments"
                        className="mobile-nav-link"
                        onClick={closeMenu}
                      >
                        SubContractor dashboard
                      </Link>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile Register Button (inside menu) */}
          {!user && (
            <div className="mobile-register-wrapper">
              <Link to="/register" className="mobile-register-btn" onClick={closeMenu}>
                Try for free
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;